import { useEffect, useRef } from "react";
import { logger } from "../../../utils/logger";

interface VisibilityChangeInfo {
  timeInBackgroundMs: number;
}

interface UseAppVisibilityOptions {
  onForeground?: (info: VisibilityChangeInfo) => void;
  onBackground?: () => void;
}

/**
 * 🔄 Hook para manejar el estado de la app en segundo plano
 * Detecta cuando la app vuelve del segundo plano y verifica el estado del calendario
 */
export const useAppVisibility = (options: UseAppVisibilityOptions = {}) => {
  const { onForeground, onBackground } = options;

  const lastVisibilityChange = useRef<number>(Date.now());
  const isInBackground = useRef<boolean>(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      const timeInBackground = now - lastVisibilityChange.current;

      if (document.visibilityState === "visible") {
        // App volvió del segundo plano
        logger.info("📱 [VISIBILITY] App volvió del segundo plano", {
          timeInBackground: `${timeInBackground}ms`,
          timestamp: now,
        });

        isInBackground.current = false;

        // Ejecutar callback personalizado
        if (onForeground) {
          onForeground({ timeInBackgroundMs: timeInBackground });
        }
      } else {
        // App fue al segundo plano
        logger.debug("📱 [VISIBILITY] App fue al segundo plano");
        lastVisibilityChange.current = now;
        isInBackground.current = true;

        // Ejecutar callback personalizado
        if (onBackground) {
          onBackground();
        }
      }
    };

    // Agregar event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onForeground, onBackground]);

  return {
    isInBackground: isInBackground.current,
    lastVisibilityChange: lastVisibilityChange.current,
  };
};
